import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/lenisManager";
import { HERO_IMAGE, COPY } from "@/lib/site";
import { MaskLine } from "./Motion";
import { Img } from "./Img";

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative grain min-h-[100svh] pt-24 md:pt-28 pb-16 flex items-center"
    >
      <div className="container-editorial w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end">
          {/* Text column */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <p className="kicker mb-6 md:mb-8 rise" style={{ animationDelay: "0.15s" }}>
              {COPY.hero.kicker}
            </p>

            <h1 className="font-display text-ink text-[3.4rem] leading-[0.95] sm:text-7xl lg:text-[6.6rem] tracking-[-0.02em]">
              <MaskLine delay={0.1}>{COPY.hero.headlineTop}</MaskLine>
              <MaskLine delay={0.24} className="italic text-clay">
                {COPY.hero.headlineBottom}
              </MaskLine>
            </h1>

            <p
              className="mt-8 max-w-xl text-base md:text-lg font-light leading-relaxed text-ink/75 rise"
              style={{ animationDelay: "0.15s" }}
            >
              {COPY.hero.body}
            </p>

            <div
              className="mt-10 flex flex-wrap items-center gap-4 rise"
              style={{ animationDelay: "0.2s" }}
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollToId("contact")}
                className="btn-primary"
              >
                {COPY.hero.ctaPrimary}
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollToId("work")}
                className="btn-ghost"
              >
                {COPY.hero.ctaSecondary}
              </button>
            </div>
          </div>

          {/* Image column */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative overflow-hidden rounded-sm aspect-[3/4] lg:aspect-[3/4.2] img-reveal">
              <motion.div style={{ y, scale }} className="absolute inset-0">
                <Img
                  name={HERO_IMAGE}
                  alt="Sona, a food, travel and lifestyle creator from Puri, Odisha, holding a camera on a city street"
                  loading="eager"
                  position="center 22%"
                  imgClassName="w-full h-full"
                />
              </motion.div>
              <div className="absolute left-4 bottom-4 right-4 flex items-end justify-between">
                <span className="kicker text-cream/90 !text-[0.62rem] backdrop-blur-[2px]">
                  {COPY.hero.locationTag}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* scroll cue */}
        <button
          data-testid="hero-scroll-cue"
          onClick={() => scrollToId("performance")}
          className="hidden lg:flex items-center gap-3 mt-14 kicker rise"
          style={{ animationDelay: "0.25s" }}
          aria-label="Scroll to performance"
        >
          <motion.span
            animate={reduce ? {} : { y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="inline-flex"
          >
            <ArrowDown size={16} />
          </motion.span>
          {COPY.hero.scrollCue}
        </button>
      </div>
    </section>
  );
};
